import { CMSUILanguage } from "./cms-types";

export type TranslationKey = 
  | "loadingPage"
  | "awardsPageBuilder"
  | "awardsPageBuilderDescription"
  | "creatingAwardsPage"
  | "errorTitle"
  | "failedToInitialize"
  | "generationFailed"
  | "translationFailed"
  | "copiedTitle"
  | "copiedDescription"
  | "prizeImages"
  | "dropImagesOrClick"
  | "generateWithAI"
  | "imagesAnalyzed"
  | "readyToAdd"
  | "imageGenerated"
  | "generationFailedShort"
  | "couldNotGenerateImage"
  | "addImagesToPage"
  | "addingToPage"
  | "generateAwardsFirst"
  | "invalidFileType"
  | "isNotImageFile"
  | "fileTooLarge"
  | "isLargerThan10MB"
  | "iPhone"
  | "android"
  | "doneEditing"
  | "edit"
  | "copied"
  | "copyHTML"
  | "download"
  | "generatingAwardsPage"
  | "describeAwardsToPreview"
  | "changesSaved"
  | "editsApplied"
  | "editModeEnabled"
  | "clickToEdit"
  | "copiedToClipboard"
  | "htmlCopied"
  | "failedToCopy"
  | "tryAgainOrDownload"
  | "downloaded"
  | "htmlDownloaded"
  | "chatPlaceholder"
  | "quickPrompt1"
  | "quickPrompt2"
  | "quickPrompt3"
  | "quickPrompt4"
  | "selectSections"
  | "selectSectionsHint"
  | "selectSectionsDescription"
  | "presetEditionEnd"
  | "presetEditionEndDesc"
  | "presetChallengeEnd"
  | "presetChallengeEndDesc"
  | "presetGuaranteedPrizes"
  | "presetGuaranteedPrizesDesc"
  | "presetLottery"
  | "presetLotteryDesc"
  | "presetNoPrizes"
  | "presetNoPrizesDesc"
  | "presetCharityOnly"
  | "presetCharityOnlyDesc"
  | "addPrizeImages"
  | "addPrizeImagesDescription"
  | "upload"
  | "aiGenerate"
  | "dropImagesHere"
  | "browse"
  | "uploadMultipleFiles"
  | "imagesReady"
  | "processing"
  | "failed"
  | "clearAll"
  | "waiting"
  | "uploading"
  | "analyzing"
  | "uploaded"
  | "analyzed"
  | "error"
  | "uploadFailed"
  | "describeImageYouWant"
  | "imagePlaceholder"
  | "generating"
  | "generateAndAnalyze"
  | "aiWillGenerateAndAnalyze"
  | "generatedImages"
  | "cancel"
  | "addImages"
  | "imagesAdded"
  | "imagesWillBeMatched"
  | "analysisFailedMessage"
  | "encounterError"
  | "somethingWentWrong"
  | "regeneratePage"
  | "editModeIndicator"
  | "aiGeneratedFilename";

type Translations = Record<CMSUILanguage, Record<TranslationKey, string>>;

export const translations: Translations = {
  en: {
    loadingPage: "Loading your page...",
    awardsPageBuilder: "Awards Page Builder",
    awardsPageBuilderDescription: "Describe your award schema in natural language and I'll create a beautiful, mobile-optimized HTML page for your Activy app.",
    creatingAwardsPage: "Creating your awards page...",
    errorTitle: "Error",
    failedToInitialize: "Failed to initialize page. Please refresh.",
    generationFailed: "Generation failed",
    translationFailed: "Translation failed",
    copiedTitle: "Copied!",
    copiedDescription: "HTML code copied to clipboard",
    prizeImages: "Prize Images",
    dropImagesOrClick: "Drop images or click to upload",
    generateWithAI: "Generate with AI...",
    imagesAnalyzed: "image(s) analyzed",
    readyToAdd: "Ready to add to your awards page",
    imageGenerated: "Image generated",
    generationFailedShort: "Generation failed",
    couldNotGenerateImage: "Could not generate image. Please try again.",
    addImagesToPage: "Add Images to Page",
    addingToPage: "Adding to page...",
    generateAwardsFirst: "Generate an awards page first, then add these images",
    invalidFileType: "Invalid file type",
    isNotImageFile: "is not an image file.",
    fileTooLarge: "File too large",
    isLargerThan10MB: "is larger than 10MB.",
    iPhone: "iPhone",
    android: "Android",
    doneEditing: "Done Editing",
    edit: "Edit",
    copied: "Copied",
    copyHTML: "Copy HTML",
    download: "Download",
    generatingAwardsPage: "Generating your awards page...",
    describeAwardsToPreview: "Describe your awards to see a preview",
    changesSaved: "Changes saved",
    editsApplied: "Your edits have been applied to the HTML.",
    editModeEnabled: "Edit mode enabled",
    clickToEdit: "Click on any text to edit. Toggle off to save.",
    copiedToClipboard: "Copied to clipboard",
    htmlCopied: "HTML code has been copied. Paste it into your Activy CMS.",
    failedToCopy: "Failed to copy",
    tryAgainOrDownload: "Please try again or use the download option.",
    downloaded: "Downloaded",
    htmlDownloaded: "Your awards page HTML has been downloaded.",
    chatPlaceholder: "Describe your award schema... e.g., 'Create a running challenge with 1st, 2nd, 3rd place prizes'",
    quickPrompt1: "Create a fitness challenge with 3 prizes",
    quickPrompt2: "Monthly cycling competition awards",
    quickPrompt3: "Team walking challenge with rankings",
    quickPrompt4: "Running race podium with medals",
    selectSections: "Select all sections that apply",
    selectSectionsHint: "Describe award details precisely in your prompt",
    selectSectionsDescription: "Choose which sections to include in your awards page. You can select multiple options.",
    presetEditionEnd: "End of edition",
    presetEditionEndDesc: "Ranking and prizes at the end of each edition",
    presetChallengeEnd: "End of challenge",
    presetChallengeEndDesc: "Final ranking and prizes at the end of the entire challenge",
    presetGuaranteedPrizes: "Guaranteed prizes",
    presetGuaranteedPrizesDesc: "Prizes that every participant can earn by meeting criteria",
    presetLottery: "Lottery draw",
    presetLotteryDesc: "Random prize drawing among participants",
    presetNoPrizes: "No prizes",
    presetNoPrizesDesc: "Challenge focused on activity and competition without prizes",
    presetCharityOnly: "Charity goal",
    presetCharityOnlyDesc: "Challenge where activity contributes to a charitable cause",
    addPrizeImages: "Add Prize Images",
    addPrizeImagesDescription: "Upload multiple images and AI will recognize what each one shows and match them to your award categories.",
    upload: "Upload",
    aiGenerate: "AI Generate",
    dropImagesHere: "Drop images here, or",
    browse: "browse",
    uploadMultipleFiles: "Upload multiple PNG, JPG or WebP files (up to 10MB each)",
    imagesReady: "ready",
    processing: "processing",
    failed: "failed",
    clearAll: "Clear all",
    waiting: "Waiting...",
    uploading: "Uploading...",
    analyzing: "Analyzing...",
    uploaded: "Uploaded",
    analyzed: "Analyzed",
    error: "Error",
    uploadFailed: "Upload failed",
    describeImageYouWant: "Describe the image you want",
    imagePlaceholder: "e.g., Garmin Fenix 7 smartwatch, golden trophy, bicycle",
    generating: "Generating...",
    generateAndAnalyze: "Generate & Analyze",
    aiWillGenerateAndAnalyze: "AI will generate an image and analyze it for matching",
    generatedImages: "Generated images",
    cancel: "Cancel",
    addImages: "Add Images",
    imagesAdded: "Images added",
    imagesWillBeMatched: "image(s) will be matched to your awards.",
    analysisFailedMessage: "Analysis failed",
    encounterError: "I encountered an error while generating your awards page. Please try again or rephrase your request.",
    somethingWentWrong: "Something went wrong. Please try again.",
    regeneratePage: "Regenerate the current page",
    editModeIndicator: "EDIT MODE",
    aiGeneratedFilename: "AI Generated",
  },
  pl: {
    loadingPage: "Ładowanie strony...",
    awardsPageBuilder: "Kreator Stron Nagród",
    awardsPageBuilderDescription: "Opisz swój schemat nagród w języku naturalnym, a ja stworzę piękną, zoptymalizowaną pod urządzenia mobilne stronę HTML dla Twojej aplikacji Activy.",
    creatingAwardsPage: "Tworzenie strony nagród...",
    errorTitle: "Błąd",
    failedToInitialize: "Nie udało się zainicjować strony. Proszę odświeżyć.",
    generationFailed: "Generowanie nie powiodło się",
    translationFailed: "Tłumaczenie nie powiodło się",
    copiedTitle: "Skopiowano!",
    copiedDescription: "Kod HTML skopiowany do schowka",
    prizeImages: "Obrazy Nagród",
    dropImagesOrClick: "Upuść obrazy lub kliknij, aby przesłać",
    generateWithAI: "Generuj z AI...",
    imagesAnalyzed: "obraz(ów) przeanalizowano",
    readyToAdd: "Gotowe do dodania na stronę nagród",
    imageGenerated: "Obraz wygenerowany",
    generationFailedShort: "Generowanie nie powiodło się",
    couldNotGenerateImage: "Nie można wygenerować obrazu. Spróbuj ponownie.",
    addImagesToPage: "Dodaj Obrazy do Strony",
    addingToPage: "Dodawanie do strony...",
    generateAwardsFirst: "Najpierw wygeneruj stronę nagród, a następnie dodaj te obrazy",
    invalidFileType: "Nieprawidłowy typ pliku",
    isNotImageFile: "nie jest plikiem obrazu.",
    fileTooLarge: "Plik zbyt duży",
    isLargerThan10MB: "jest większy niż 10MB.",
    iPhone: "iPhone",
    android: "Android",
    doneEditing: "Zakończ Edycję",
    edit: "Edytuj",
    copied: "Skopiowano",
    copyHTML: "Kopiuj HTML",
    download: "Pobierz",
    generatingAwardsPage: "Generowanie strony nagród...",
    describeAwardsToPreview: "Opisz swoje nagrody, aby zobaczyć podgląd",
    changesSaved: "Zmiany zapisane",
    editsApplied: "Twoje zmiany zostały zastosowane do HTML.",
    editModeEnabled: "Tryb edycji włączony",
    clickToEdit: "Kliknij na dowolny tekst, aby edytować. Wyłącz, aby zapisać.",
    copiedToClipboard: "Skopiowano do schowka",
    htmlCopied: "Kod HTML został skopiowany. Wklej go do swojego Activy CMS.",
    failedToCopy: "Kopiowanie nie powiodło się",
    tryAgainOrDownload: "Spróbuj ponownie lub użyj opcji pobierania.",
    downloaded: "Pobrano",
    htmlDownloaded: "Twoja strona nagród HTML została pobrana.",
    chatPlaceholder: "Opisz swój schemat nagród... np. 'Stwórz wyzwanie biegowe z nagrodami za 1, 2 i 3 miejsce'",
    quickPrompt1: "Stwórz wyzwanie fitness z 3 nagrodami",
    quickPrompt2: "Nagrody za miesięczne zawody kolarskie",
    quickPrompt3: "Wyzwanie zespołowe z chodzeniem i rankingiem",
    quickPrompt4: "Podium wyścigu biegowego z medalami",
    selectSections: "Wybierz wszystkie pasujące sekcje",
    selectSectionsHint: "Szczegóły nagradzania wpisz precyzyjnie w prompt",
    selectSectionsDescription: "Wybierz, które sekcje mają być na stronie nagród. Możesz wybrać wiele opcji.",
    presetEditionEnd: "Na koniec edycji",
    presetEditionEndDesc: "Ranking i nagrody na koniec każdej edycji",
    presetChallengeEnd: "Na koniec wyzwania",
    presetChallengeEndDesc: "Końcowy ranking i nagrody na koniec całego wyzwania",
    presetGuaranteedPrizes: "Nagrody gwarantowane",
    presetGuaranteedPrizesDesc: "Nagrody, które każdy uczestnik może zdobyć spełniając kryteria",
    presetLottery: "Losowanie",
    presetLotteryDesc: "Losowanie nagród wśród uczestników",
    presetNoPrizes: "Brak nagród",
    presetNoPrizesDesc: "Wyzwanie skupione na aktywności i rywalizacji bez nagród",
    presetCharityOnly: "Cel charytatywny",
    presetCharityOnlyDesc: "Wyzwanie, w którym aktywność wspiera cel charytatywny",
    addPrizeImages: "Dodaj Obrazy Nagród",
    addPrizeImagesDescription: "Prześlij wiele obrazów, a AI rozpozna, co każdy z nich przedstawia i dopasuje je do kategorii nagród.",
    upload: "Prześlij",
    aiGenerate: "Generuj AI",
    dropImagesHere: "Upuść obrazy tutaj lub",
    browse: "przeglądaj",
    uploadMultipleFiles: "Prześlij wiele plików PNG, JPG lub WebP (do 10MB każdy)",
    imagesReady: "gotowe",
    processing: "przetwarzanie",
    failed: "niepowodzenie",
    clearAll: "Wyczyść wszystko",
    waiting: "Oczekiwanie...",
    uploading: "Przesyłanie...",
    analyzing: "Analizowanie...",
    uploaded: "Przesłano",
    analyzed: "Przeanalizowano",
    error: "Błąd",
    uploadFailed: "Przesyłanie nie powiodło się",
    describeImageYouWant: "Opisz obraz, który chcesz",
    imagePlaceholder: "np. zegarek Garmin Fenix 7, złoty puchar, rower",
    generating: "Generowanie...",
    generateAndAnalyze: "Generuj i Analizuj",
    aiWillGenerateAndAnalyze: "AI wygeneruje obraz i przeanalizuje go do dopasowania",
    generatedImages: "Wygenerowane obrazy",
    cancel: "Anuluj",
    addImages: "Dodaj Obrazy",
    imagesAdded: "Obrazy dodane",
    imagesWillBeMatched: "obraz(ów) zostanie dopasowanych do nagród.",
    analysisFailedMessage: "Analiza nie powiodła się",
    encounterError: "Wystąpił błąd podczas generowania strony nagród. Spróbuj ponownie lub przeformułuj swoje żądanie.",
    somethingWentWrong: "Coś poszło nie tak. Spróbuj ponownie.",
    regeneratePage: "Wygeneruj ponownie bieżącą stronę",
    editModeIndicator: "TRYB EDYCJI",
    aiGeneratedFilename: "Wygenerowano przez AI",
  },
  es: {
    loadingPage: "Cargando tu página...",
    awardsPageBuilder: "Constructor de Páginas de Premios",
    awardsPageBuilderDescription: "Describe tu esquema de premios en lenguaje natural y crearé una hermosa página HTML optimizada para móviles para tu aplicación Activy.",
    creatingAwardsPage: "Creando tu página de premios...",
    errorTitle: "Error",
    failedToInitialize: "No se pudo inicializar la página. Por favor, actualiza.",
    generationFailed: "La generación falló",
    translationFailed: "La traducción falló",
    copiedTitle: "¡Copiado!",
    copiedDescription: "Código HTML copiado al portapapeles",
    prizeImages: "Imágenes de Premios",
    dropImagesOrClick: "Suelta imágenes o haz clic para subir",
    generateWithAI: "Generar con IA...",
    imagesAnalyzed: "imagen(es) analizadas",
    readyToAdd: "Listo para añadir a tu página de premios",
    imageGenerated: "Imagen generada",
    generationFailedShort: "Generación fallida",
    couldNotGenerateImage: "No se pudo generar la imagen. Por favor, inténtalo de nuevo.",
    addImagesToPage: "Añadir Imágenes a la Página",
    addingToPage: "Añadiendo a la página...",
    generateAwardsFirst: "Primero genera una página de premios, luego añade estas imágenes",
    invalidFileType: "Tipo de archivo inválido",
    isNotImageFile: "no es un archivo de imagen.",
    fileTooLarge: "Archivo demasiado grande",
    isLargerThan10MB: "es mayor de 10MB.",
    iPhone: "iPhone",
    android: "Android",
    doneEditing: "Terminar Edición",
    edit: "Editar",
    copied: "Copiado",
    copyHTML: "Copiar HTML",
    download: "Descargar",
    generatingAwardsPage: "Generando tu página de premios...",
    describeAwardsToPreview: "Describe tus premios para ver una vista previa",
    changesSaved: "Cambios guardados",
    editsApplied: "Tus ediciones se han aplicado al HTML.",
    editModeEnabled: "Modo de edición habilitado",
    clickToEdit: "Haz clic en cualquier texto para editar. Desactiva para guardar.",
    copiedToClipboard: "Copiado al portapapeles",
    htmlCopied: "El código HTML ha sido copiado. Pégalo en tu Activy CMS.",
    failedToCopy: "Error al copiar",
    tryAgainOrDownload: "Por favor, inténtalo de nuevo o usa la opción de descarga.",
    downloaded: "Descargado",
    htmlDownloaded: "Tu página HTML de premios ha sido descargada.",
    chatPlaceholder: "Describe tu esquema de premios... ej., 'Crea un desafío de carrera con premios para 1er, 2do y 3er lugar'",
    quickPrompt1: "Crea un desafío fitness con 3 premios",
    quickPrompt2: "Premios de competencia de ciclismo mensual",
    quickPrompt3: "Desafío de caminata en equipo con rankings",
    quickPrompt4: "Podio de carrera con medallas",
    selectSections: "Selecciona todas las secciones que apliquen",
    selectSectionsHint: "Describe los detalles de los premios con precisión en tu prompt",
    selectSectionsDescription: "Elige qué secciones incluir en tu página de premios. Puedes seleccionar múltiples opciones.",
    presetEditionEnd: "Fin de edición",
    presetEditionEndDesc: "Ranking y premios al final de cada edición",
    presetChallengeEnd: "Fin del desafío",
    presetChallengeEndDesc: "Ranking final y premios al final de todo el desafío",
    presetGuaranteedPrizes: "Premios garantizados",
    presetGuaranteedPrizesDesc: "Premios que cada participante puede ganar al cumplir criterios",
    presetLottery: "Sorteo",
    presetLotteryDesc: "Sorteo aleatorio de premios entre participantes",
    presetNoPrizes: "Sin premios",
    presetNoPrizesDesc: "Desafío enfocado en actividad y competencia sin premios",
    presetCharityOnly: "Objetivo benéfico",
    presetCharityOnlyDesc: "Desafío donde la actividad contribuye a una causa benéfica",
    addPrizeImages: "Añadir Imágenes de Premios",
    addPrizeImagesDescription: "Sube múltiples imágenes y la IA reconocerá lo que muestra cada una y las emparejará con tus categorías de premios.",
    upload: "Subir",
    aiGenerate: "Generar IA",
    dropImagesHere: "Suelta imágenes aquí, o",
    browse: "explorar",
    uploadMultipleFiles: "Sube múltiples archivos PNG, JPG o WebP (hasta 10MB cada uno)",
    imagesReady: "listas",
    processing: "procesando",
    failed: "fallido",
    clearAll: "Limpiar todo",
    waiting: "Esperando...",
    uploading: "Subiendo...",
    analyzing: "Analizando...",
    uploaded: "Subido",
    analyzed: "Analizado",
    error: "Error",
    uploadFailed: "Subida fallida",
    describeImageYouWant: "Describe la imagen que quieres",
    imagePlaceholder: "ej., reloj Garmin Fenix 7, trofeo dorado, bicicleta",
    generating: "Generando...",
    generateAndAnalyze: "Generar y Analizar",
    aiWillGenerateAndAnalyze: "La IA generará una imagen y la analizará para emparejar",
    generatedImages: "Imágenes generadas",
    cancel: "Cancelar",
    addImages: "Añadir Imágenes",
    imagesAdded: "Imágenes añadidas",
    imagesWillBeMatched: "imagen(es) se emparejarán con tus premios.",
    analysisFailedMessage: "Análisis fallido",
    encounterError: "Encontré un error al generar tu página de premios. Por favor, inténtalo de nuevo o reformula tu solicitud.",
    somethingWentWrong: "Algo salió mal. Por favor, inténtalo de nuevo.",
    regeneratePage: "Regenerar la página actual",
    editModeIndicator: "MODO EDICIÓN",
    aiGeneratedFilename: "Generado por IA",
  },
};

export function getTranslation(language: CMSUILanguage, key: TranslationKey): string {
  return translations[language]?.[key] || translations.en[key] || key;
}

export function useTranslations(language: CMSUILanguage) {
  return {
    t: (key: TranslationKey) => getTranslation(language, key),
    language,
  };
}
